import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Document, Packer, Paragraph, TextRun, AlignmentType, ImageRun } from 'docx';
import cfgLogo from '@/assets/cfg-logo.png';

interface BigPurchaseData {
  dealId?: string;
  // Seller Info
  sellerName: string;
  sellerAddress: string;
  // Buyer Info
  buyerName: string;
  buyerAddress: string;
  // Purchaser (under Land Contract)
  purchaserName: string;
  // Land Contract Details
  landContractDate: string;
  currentBalance: string;
  balanceAsOfDate: string;
  interestRate: string;
  propertyAddress: string;
  // Payment Terms
  paymentAtClosing: string;
  firstPaymentDate: string;
  // Regulatory Info
  sellerCreditorDate: string;
  // Agreement Info
  agreementDate: string;
  // Drafting Info
  draftedByName: string;
  draftedByCompany: string;
  draftedByAddress: string;
  mailingAddress: string;
  referenceNumber: string;
  // Cancellation Fee
  cancellationFee: string;
}

const formatCurrency = (value: string): string => {
  const num = parseFloat(value || "0");
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (dateStr: string): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

export function BigPurchaseAgreementGenerator() {
  const { toast } = useToast();
  const [deals, setDeals] = useState<any[]>([]);
  const [formData, setFormData] = useState<BigPurchaseData>(() => {
    const saved = localStorage.getItem('bigPurchaseProgress');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error loading saved form data:', e);
      }
    }
    return {
      sellerName: "",
      sellerAddress: "",
      buyerName: "First National Bank of America",
      buyerAddress: "241 E. Saginaw, PO Box 980, East Lansing, MI 48826",
      purchaserName: "",
      landContractDate: "",
      currentBalance: "",
      balanceAsOfDate: new Date().toISOString().split('T')[0],
      interestRate: "",
      propertyAddress: "",
      paymentAtClosing: "",
      firstPaymentDate: "",
      sellerCreditorDate: "",
      agreementDate: new Date().toISOString().split('T')[0],
      draftedByName: "Annie Barr",
      draftedByCompany: "First National Bank of America",
      draftedByAddress: "P.O. Box 980, East Lansing, MI 48826",
      mailingAddress: "First National Bank of America\nP.O. Box 980\nEast Lansing, MI 48826",
      referenceNumber: "",
      cancellationFee: "500.00",
    };
  });

  useEffect(() => {
    loadDeals();
  }, []);

  useEffect(() => {
    localStorage.setItem('bigPurchaseProgress', JSON.stringify(formData));
  }, [formData]);

  const loadDeals = async () => {
    const { data, error } = await supabase
      .from('deals')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error loading deals:', error);
      return;
    }
    setDeals(data || []);
  };

  const loadDealData = async (dealId: string) => {
    const deal = deals.find(d => d.id === dealId);
    if (!deal) return;

    setFormData(prev => ({
      ...prev,
      dealId,
      sellerName: deal.seller_name || "",
      sellerAddress: deal.seller_address || "",
      purchaserName: deal.buyer_name || "",
      propertyAddress: deal.property_address || "",
      currentBalance: deal.current_balance?.toString() || "",
      interestRate: deal.interest_rate?.toString() || "",
      paymentAtClosing: deal.offer_price?.toString() || "",
      landContractDate: deal.date_of_sale || "",
      firstPaymentDate: deal.first_payment_date || "",
    }));

    toast({
      title: "Deal Loaded",
      description: `Loaded data from ${deal.deal_title}`,
    });
  };

  const clearForm = () => {
    const emptyForm: BigPurchaseData = {
      sellerName: "",
      sellerAddress: "",
      buyerName: "First National Bank of America",
      buyerAddress: "241 E. Saginaw, PO Box 980, East Lansing, MI 48826",
      purchaserName: "",
      landContractDate: "",
      currentBalance: "",
      balanceAsOfDate: new Date().toISOString().split('T')[0],
      interestRate: "",
      propertyAddress: "",
      paymentAtClosing: "",
      firstPaymentDate: "",
      sellerCreditorDate: "",
      agreementDate: new Date().toISOString().split('T')[0],
      draftedByName: "Annie Barr",
      draftedByCompany: "First National Bank of America",
      draftedByAddress: "P.O. Box 980, East Lansing, MI 48826",
      mailingAddress: "First National Bank of America\nP.O. Box 980\nEast Lansing, MI 48826",
      referenceNumber: "",
      cancellationFee: "500.00",
    };
    setFormData(emptyForm);
    localStorage.removeItem('bigPurchaseProgress');
    toast({
      title: "Form Cleared",
      description: "All fields have been reset",
    });
  };

  const generateWarrantyParagraphs = (data: BigPurchaseData): Paragraph[] => {
    const warranties = [
      "All information in this Agreement is correct.",
      "Seller holds all right, title and interest and can convey clear title to the Purchaser in the Land Contract.",
      "Seller and Purchaser have made no prior transfer or assignment of any portion of the Land Contract.",
      "There have been no oral amendments or modifications, including but not limited to adjustments in the principal balance, of any kind to the Land Contract. If such amendments or modifications have been made, a written amendment stating these changes must be executed by both the Seller and the Purchaser.",
      "The Land Contract represents a bona fide sale of real estate. The Land Contract was binding on the date written on the face.",
      "Seller has no knowledge or notice of any defenses or offsets to the full and complete payment of the Land Contract.",
      "The Land Contract does not violate any applicable Federal or state truth-in-lending, consumer protection or usury laws.",
      "The Seller has made a full and accurate disclosure of the Purchaser's payment history: the Purchaser is not in default in any provision of the Land Contract, and does not dispute the principal balance of the Land Contract.",
      "The Seller has received all payments under the Land Contract, including the down payment.",
      "The Land Contract is not bound by a mortgage servicing agreement or escrow agreement, or if said Land Contract is bound by a mortgage servicing agreement or escrow agreement the Seller agrees to terminate such agreement. Any and all payments received by said servicing company shall be given to the Buyer. All costs associated with termination of a mortgage servicing agreement or escrow agreement will be paid by the Seller.",
      "The Seller has no knowledge of the presence on the land of hazardous or environmentally harmful substances as may be defined by state or federal law.",
      `Seller was not a creditor as defined within Regulation Z and the Truth in Lending Act as of ${formatDate(data.sellerCreditorDate) || formatDate(data.landContractDate)}, the date of the contract.`,
    ];

    return warranties.map((warranty, index) => 
      new Paragraph({
        children: [
          new TextRun({ text: `    ${index + 1}. `, color: "000000" }),
          new TextRun({ text: warranty, color: "000000" }),
        ],
        spacing: { after: 50 }
      })
    );
  };

  const generateDocument = async () => {
    if (!formData.sellerName || !formData.purchaserName || !formData.propertyAddress) {
      toast({
        title: "Missing Information",
        description: "Please fill in Seller Name, Purchaser Name, and Property Address",
        variant: "destructive"
      });
      return;
    }

    try {
      // Fetch logo image
      const logoResponse = await fetch(cfgLogo);
      const logoBuffer = await logoResponse.arrayBuffer();

      const doc = new Document({
        sections: [{
          properties: {
            page: {
              margin: {
                top: 1440,
                bottom: 1440,
                left: 1440,
                right: 1440
              }
            }
          },
          children: [
            // Logo centered at top
            new Paragraph({
              children: [
                new ImageRun({
                  data: logoBuffer,
                  transformation: { width: 150, height: 180 },
                  type: 'png',
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 300 }
            }),
            // Title
            new Paragraph({
              children: [
                new TextRun({ text: "Purchase & Sale Agreement", bold: true, size: 28, color: "000000" })
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 100 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: "MI Land Contract Full Buyout", bold: true, size: 24, color: "000000" })
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 400 }
            }),

            // Opening Paragraph
            new Paragraph({
              children: [
                new TextRun({ text: `This Agreement is executed between `, color: "000000" }),
                new TextRun({ text: formData.sellerName, bold: true, color: "000000" }),
                new TextRun({ text: ` ("Seller" or "You") whose mailing address is `, color: "000000" }),
                new TextRun({ text: formData.sellerAddress, color: "000000" }),
                new TextRun({ text: `, and `, color: "000000" }),
                new TextRun({ text: formData.buyerName, bold: true, color: "000000" }),
                new TextRun({ text: `, ("Buyer" or "Us"), whose business address is `, color: "000000" }),
                new TextRun({ text: formData.buyerAddress, color: "000000" }),
                new TextRun({ text: `.`, color: "000000" }),
              ],
              spacing: { after: 400 }
            }),

            // Covenants Header
            new Paragraph({
              children: [
                new TextRun({ text: "Covenants", bold: true, size: 26, color: "000000" })
              ],
              spacing: { after: 200 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: "Buyer and Seller mutually agree and promise to the following:", color: "000000" })
              ],
              spacing: { after: 200 }
            }),

            // 1. Land Contract Identification
            new Paragraph({
              children: [
                new TextRun({ text: "1. ", bold: true, color: "000000" }),
                new TextRun({ text: "The subject Land Contract (the term \"Land Contract\" for the purposes of this Agreement shall include any note or lien integrated with the Land Contract) is identified as:", color: "000000" })
              ],
              spacing: { after: 100 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `    1. The purchaser under the Land Contract is `, color: "000000" }),
                new TextRun({ text: formData.purchaserName, bold: true, color: "000000" }),
                new TextRun({ text: ` ("Purchaser")`, color: "000000" }),
              ],
              spacing: { after: 50 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `    2. Dated `, color: "000000" }),
                new TextRun({ text: formatDate(formData.landContractDate), bold: true, color: "000000" }),
                new TextRun({ text: `,`, color: "000000" }),
              ],
              spacing: { after: 50 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `    3. Having the current balance of `, color: "000000" }),
                new TextRun({ text: `$${formatCurrency(formData.currentBalance)}`, bold: true, color: "000000" }),
                new TextRun({ text: ` as of `, color: "000000" }),
                new TextRun({ text: formatDate(formData.balanceAsOfDate), bold: true, color: "000000" }),
                new TextRun({ text: `,`, color: "000000" }),
              ],
              spacing: { after: 50 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `    4. Interest of `, color: "000000" }),
                new TextRun({ text: `${formData.interestRate}%`, bold: true, color: "000000" }),
                new TextRun({ text: `.`, color: "000000" }),
              ],
              spacing: { after: 50 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `    5. Secured by property located at `, color: "000000" }),
                new TextRun({ text: formData.propertyAddress, bold: true, color: "000000" }),
              ],
              spacing: { after: 200 }
            }),

            // 2. Promise to Buy/Sell
            new Paragraph({
              children: [
                new TextRun({ text: "2. ", bold: true, color: "000000" }),
                new TextRun({ text: "Promise to Buy/Sell. ", bold: true, color: "000000" }),
                new TextRun({ text: `Seller promises to sell and Buyer promises to buy all of the remaining payments under the Land Contract, beginning with the payment due on `, color: "000000" }),
                new TextRun({ text: formatDate(formData.firstPaymentDate), bold: true, color: "000000" }),
                new TextRun({ text: `. Payable as follows: `, color: "000000" }),
                new TextRun({ text: `$${formatCurrency(formData.paymentAtClosing)}`, bold: true, color: "000000" }),
                new TextRun({ text: ` at closing, as set forth in the final closing statement.`, color: "000000" }),
              ],
              spacing: { after: 200 }
            }),

            // 3. Warranties Header
            new Paragraph({
              children: [
                new TextRun({ text: "3. ", bold: true, color: "000000" }),
                new TextRun({ text: "Warranties. ", bold: true, color: "000000" }),
                new TextRun({ text: "The Seller makes the following representations and warranties as an inducement for the Buyer to complete the sale of the Land Contract:", color: "000000" }),
              ],
              spacing: { after: 100 }
            }),

            // Warranty items
            ...generateWarrantyParagraphs(formData),

            // 4. Seller's Cancellation
            new Paragraph({
              children: [
                new TextRun({ text: "4. ", bold: true, color: "000000" }),
                new TextRun({ text: "Seller's Cancellation. ", bold: true, color: "000000" }),
                new TextRun({ text: `Should the Seller elect to cancel this Agreement and/or should Seller not comply with standard closing procedures normally associated with the sale of a Land Contract, including, but not limited to failure to deliver the original Land Contract, failure to assign the Land Contract, Seller shall be liable for `, color: "000000" }),
                new TextRun({ text: `$${formatCurrency(formData.cancellationFee)}`, bold: true, color: "000000" }),
                new TextRun({ text: ` as liquidated damages.`, color: "000000" }),
              ],
              spacing: { after: 200 }
            }),

            // 5. Buyer's right to cancel
            new Paragraph({
              children: [
                new TextRun({ text: "5. ", bold: true, color: "000000" }),
                new TextRun({ text: "Buyer's right to cancel. ", bold: true, color: "000000" }),
                new TextRun({ text: `Buyer has the right to cancel this Agreement should it deem, in its sole and absolute discretion, any one of the following insufficient: (a) Purchaser's Credit, (b) Purchaser's payment history, (c) title problems, (d) disparity between Seller's statement of condition and Purchaser's statement of condition as per the Purchaser Interview, (e) terms and conditions found in the actual Land Contract, not previously revealed by Seller, (f) inadequate Purchaser Interview, (g) adverse property appraisal.`, color: "000000" }),
              ],
              spacing: { after: 200 }
            }),

            // 6. Seller's Cooperation
            new Paragraph({
              children: [
                new TextRun({ text: "6. ", bold: true, color: "000000" }),
                new TextRun({ text: "Seller's Cooperation. ", bold: true, color: "000000" }),
                new TextRun({ text: `In order to proceed in a timely fashion to close this Land Contract sale, Seller agrees to assist Buyer by providing Buyer with (including but not limited to) copies of subject Land Contract, closing statement, payment history, hazard insurance coverage, Purchaser's address, any other reasonable information that will enable Buyer to complete its due diligence prior to closing, and to provide originals of the Land Contract at closing. Failure to provide these originals may delay the funding of the transaction.`, color: "000000" }),
              ],
              spacing: { after: 200 }
            }),

            // 7. Modification
            new Paragraph({
              children: [
                new TextRun({ text: "7. ", bold: true, color: "000000" }),
                new TextRun({ text: "Modification. ", bold: true, color: "000000" }),
                new TextRun({ text: "This Agreement represents the entire contract between Buyer and Seller. This Agreement may only be amended by a written document, signed by the authorized representative of Buyer.", color: "000000" }),
              ],
              spacing: { after: 200 }
            }),

            // 8. Assignability
            new Paragraph({
              children: [
                new TextRun({ text: "8. ", bold: true, color: "000000" }),
                new TextRun({ text: "Assignability. ", bold: true, color: "000000" }),
                new TextRun({ text: "We may assign this Agreement or any part of it without notice to you.", color: "000000" }),
              ],
              spacing: { after: 200 }
            }),

            // 9. Time of the Essence
            new Paragraph({
              children: [
                new TextRun({ text: "9. ", bold: true, color: "000000" }),
                new TextRun({ text: "Time of the Essence. ", bold: true, color: "000000" }),
                new TextRun({ text: "Time is of the essence in respect to this Agreement.", color: "000000" }),
              ],
              spacing: { after: 200 }
            }),

            // 10. Acknowledgment of Seller
            new Paragraph({
              children: [
                new TextRun({ text: "10. ", bold: true, color: "000000" }),
                new TextRun({ text: "Acknowledgment of Seller. ", bold: true, color: "000000" }),
                new TextRun({ text: "The Seller specifically acknowledges and agrees that this agreement is not a loan to you, and has not been characterized by us as a loan. Rather, it is the purchase of a stream of payments that are due to you from the Purchaser described above.", color: "000000" }),
              ],
              spacing: { after: 200 }
            }),

            // 11. Permission to Follow Up
            new Paragraph({
              children: [
                new TextRun({ text: "11. ", bold: true, color: "000000" }),
                new TextRun({ text: "Permission to Follow Up. ", bold: true, color: "000000" }),
                new TextRun({ text: `I (we) hereby allow ${formData.buyerName.split(',')[0]}, to follow up on this or other quotes for a period of three years by telephone. I understand I can remove this permission at any time for any reason.`, color: "000000" }),
              ],
              spacing: { after: 200 }
            }),

            // 12. Cooperation of Seller
            new Paragraph({
              children: [
                new TextRun({ text: "12. ", bold: true, color: "000000" }),
                new TextRun({ text: "Cooperation of Seller. ", bold: true, color: "000000" }),
                new TextRun({ text: "Seller agrees to cooperate with Buyer within a reasonable time to re-execute any, and all, document(s), required in the event that any, or all of, the original document(s) necessary to close the transaction have been lost, destroyed, or are otherwise missing.", color: "000000" }),
              ],
              spacing: { after: 400 }
            }),

            // Signature Section
            new Paragraph({
              children: [
                new TextRun({ text: "Signed by:", bold: true, color: "000000" })
              ],
              spacing: { after: 200 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: "", color: "000000" })
              ],
              spacing: { after: 100 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: "_______________________________", color: "000000" })
              ],
              spacing: { after: 10 }
            }),
            ...formData.sellerName.split(' and ').map(name => 
              new Paragraph({
                children: [
                  new TextRun({ text: name.trim(), color: "000000" })
                ],
                spacing: { after: 200 }
              })
            ),

            // Drafted By Section
            new Paragraph({
              children: [
                new TextRun({ text: "Drafted By:", bold: true, color: "000000" })
              ],
              spacing: { before: 400, after: 100 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: formData.draftedByName, color: "000000" })
              ],
              spacing: { after: 50 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: formData.draftedByCompany, color: "000000" })
              ],
              spacing: { after: 50 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: formData.draftedByAddress, color: "000000" })
              ],
              spacing: { after: 200 }
            }),

            // Mailing Address
            new Paragraph({
              children: [
                new TextRun({ text: "Mailing Address", bold: true, color: "000000" })
              ],
              spacing: { before: 200, after: 100 }
            }),
            ...formData.mailingAddress.split('\n').map(line =>
              new Paragraph({
                children: [
                  new TextRun({ text: line, color: "000000" })
                ],
                spacing: { after: 50 }
              })
            ),

            // Reference Number
            ...(formData.referenceNumber ? [
              new Paragraph({
                children: [
                  new TextRun({ text: formData.referenceNumber, color: "000000" })
                ],
                spacing: { before: 200, after: 100 }
              })
            ] : []),
          ]
        }]
      });

      const blob = await Packer.toBlob(doc);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeSellerName = formData.sellerName.replace(/[^a-zA-Z0-9]/g, '_');
      link.download = `Purchase_Agreement_${safeSellerName}_${new Date().toISOString().split('T')[0]}.docx`;
      link.click();
      window.URL.revokeObjectURL(url);

      toast({
        title: "Success",
        description: "Big Purchase Agreement generated successfully!"
      });
    } catch (error) {
      console.error('Error generating document:', error);
      toast({
        title: "Error",
        description: "Failed to generate document",
        variant: "destructive"
      });
    }
  };

  const handleChange = (field: keyof BigPurchaseData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Big Purchase Agreement Generator</span>
          <Button variant="outline" size="sm" onClick={clearForm}>
            Clear Form
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Load from Deal */}
        <div className="space-y-2">
          <Label>Load from Deal (Optional)</Label>
          <Select onValueChange={loadDealData}>
            <SelectTrigger>
              <SelectValue placeholder="Select a deal to auto-fill..." />
            </SelectTrigger>
            <SelectContent>
              {deals.map(deal => (
                <SelectItem key={deal.id} value={deal.id}>
                  {deal.deal_title} - {deal.buyer_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Seller Information */}
        <div className="space-y-3">
          <h3 className="font-semibold text-lg border-b pb-2">Seller Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Seller Name(s) *</Label>
              <Input
                value={formData.sellerName}
                onChange={(e) => handleChange('sellerName', e.target.value)}
                placeholder="John R. Murray and Shelly J. Murray"
              />
            </div>
            <div className="space-y-2">
              <Label>Seller Mailing Address</Label>
              <Input
                value={formData.sellerAddress}
                onChange={(e) => handleChange('sellerAddress', e.target.value)}
                placeholder="11725 Gates Road, Mulliken, MI 48861"
              />
            </div>
          </div>
        </div>

        {/* Buyer Information */}
        <div className="space-y-3">
          <h3 className="font-semibold text-lg border-b pb-2">Buyer Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Buyer Name (Company)</Label>
              <Input
                value={formData.buyerName}
                onChange={(e) => handleChange('buyerName', e.target.value)}
                placeholder="First National Bank of America"
              />
            </div>
            <div className="space-y-2">
              <Label>Buyer Business Address</Label>
              <Input
                value={formData.buyerAddress}
                onChange={(e) => handleChange('buyerAddress', e.target.value)}
                placeholder="241 E. Saginaw, PO Box 980, East Lansing, MI 48826"
              />
            </div>
          </div>
        </div>

        {/* Land Contract Details */}
        <div className="space-y-3">
          <h3 className="font-semibold text-lg border-b pb-2">Land Contract Details</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Purchaser Name (Buyer under Land Contract) *</Label>
              <Input
                value={formData.purchaserName}
                onChange={(e) => handleChange('purchaserName', e.target.value)}
                placeholder="James A. Nelson"
              />
            </div>
            <div className="space-y-2">
              <Label>Land Contract Date</Label>
              <Input
                type="date"
                value={formData.landContractDate}
                onChange={(e) => handleChange('landContractDate', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Current Balance</Label>
              <Input
                value={formData.currentBalance}
                onChange={(e) => handleChange('currentBalance', e.target.value)}
                placeholder="115445.96"
              />
            </div>
            <div className="space-y-2">
              <Label>Balance As Of Date</Label>
              <Input
                type="date"
                value={formData.balanceAsOfDate}
                onChange={(e) => handleChange('balanceAsOfDate', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Interest Rate (%)</Label>
              <Input
                value={formData.interestRate}
                onChange={(e) => handleChange('interestRate', e.target.value)}
                placeholder="8"
              />
            </div>
            <div className="space-y-2">
              <Label>Property Address *</Label>
              <Input
                value={formData.propertyAddress}
                onChange={(e) => handleChange('propertyAddress', e.target.value)}
                placeholder="1070 E St Joe Hwy Grand Ledge, MI 48837"
              />
            </div>
          </div>
        </div>

        {/* Payment Terms */}
        <div className="space-y-3">
          <h3 className="font-semibold text-lg border-b pb-2">Payment Terms</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Payment at Closing</Label>
              <Input
                value={formData.paymentAtClosing}
                onChange={(e) => handleChange('paymentAtClosing', e.target.value)}
                placeholder="100600.00"
              />
            </div>
            <div className="space-y-2">
              <Label>First Payment Date</Label>
              <Input
                type="date"
                value={formData.firstPaymentDate}
                onChange={(e) => handleChange('firstPaymentDate', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Cancellation Fee</Label>
              <Input
                value={formData.cancellationFee}
                onChange={(e) => handleChange('cancellationFee', e.target.value)}
                placeholder="500.00"
              />
            </div>
            <div className="space-y-2">
              <Label>Seller Creditor Date (for Reg Z)</Label>
              <Input
                type="date"
                value={formData.sellerCreditorDate}
                onChange={(e) => handleChange('sellerCreditorDate', e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Date seller was not a creditor under Reg Z (defaults to Land Contract date)</p>
            </div>
          </div>
        </div>

        {/* Drafting Information */}
        <div className="space-y-3">
          <h3 className="font-semibold text-lg border-b pb-2">Drafting Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Drafted By (Name)</Label>
              <Input
                value={formData.draftedByName}
                onChange={(e) => handleChange('draftedByName', e.target.value)}
                placeholder="Annie Barr"
              />
            </div>
            <div className="space-y-2">
              <Label>Company</Label>
              <Input
                value={formData.draftedByCompany}
                onChange={(e) => handleChange('draftedByCompany', e.target.value)}
                placeholder="First National Bank of America"
              />
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={formData.draftedByAddress}
                onChange={(e) => handleChange('draftedByAddress', e.target.value)}
                placeholder="P.O. Box 980, East Lansing, MI 48826"
              />
            </div>
            <div className="space-y-2">
              <Label>Reference Number</Label>
              <Input
                value={formData.referenceNumber}
                onChange={(e) => handleChange('referenceNumber', e.target.value)}
                placeholder="586002"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Mailing Address (Multi-line)</Label>
            <Textarea
              value={formData.mailingAddress}
              onChange={(e) => handleChange('mailingAddress', e.target.value)}
              rows={3}
              placeholder="First National Bank of America&#10;P.O. Box 980&#10;East Lansing, MI 48826"
            />
          </div>
        </div>

        {/* Generate Button */}
        <Button onClick={generateDocument} className="w-full" size="lg">
          <Download className="mr-2 h-5 w-5" />
          Generate Big Purchase Agreement
        </Button>
      </CardContent>
    </Card>
  );
}
